import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import { FormPilihanBelajarRuangGTK } from 'app/KinerjaPage/pages/PelaksanaanV2/components/FormPilihanBelajarRuangGTK'
import {
  API_GET_FOLLOW_UP_DOCUMENT_QUERY_KEY,
  useAPIGetFollowUpDocument,
  useAPIPelaksanaanProgress,
  useAPIUpsertFollowUpDocument,
} from 'api/kinerja/useAPIKinerja'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import {
  Card,
  CardBody,
} from 'app/KinerjaPage/pages/PelaksanaanV2/components/Card'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { SaveToDraftDialog } from 'app/KinerjaPage/pages/PelaksanaanV2/components/SaveToDraftDialog'
import { SubmitConfirmationDialog } from 'app/KinerjaPage/pages/PelaksanaanV2/components/SubmitConfirmationDialog'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { PENGELOLAAN_KINERJA_PELAKSANAAN_STEPS } from 'app/KinerjaShared/constants/pelaksanaanSteps'
import { FormPilihanBelajarLainnya } from 'app/KinerjaPage/pages/PelaksanaanV2/components/FormPilihanBelajarLainnya'
import { FormTindakLanjutObservasiKelas } from '../components/FormTindakLanjutObservasiKelas'
import useBeforePopState from '../hooks/useBeforePopState'
import { generateErrorMessage, getErrorCode } from 'app/KinerjaShared/utils/api'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'

import type { CustomError } from 'utils/customError'
import type { IFollowUpDocumentForm } from 'types/kinerja-pelaksanaan'

type DokumenTindakLanjutStep2Props = {
  isViewOnly?: boolean
  overridePeriodPayload?: boolean
  period?: number
  year?: number
  ptkId?: string
  disableTracker?: boolean
}

export function DokumenTindakLanjutStep2({
  isViewOnly = false,
  overridePeriodPayload = false,
  period,
  year,
  ptkId,
  disableTracker = false,
}: DokumenTindakLanjutStep2Props) {
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { trackEvent } = useSKPPelaksanaanTrackEvent(
    overridePeriodPayload ? { period, year } : undefined
  )
  const CURRENT_SCREEN_NAME = 'form_c'

  const periodParams = overridePeriodPayload
    ? { skp_period: period, skp_year: year }
    : undefined

  const methods = useForm<IFollowUpDocumentForm>({
    mode: 'onChange',
    defaultValues: {
      selfReflection: '',
      followUpTargets: [],
      supportNeed: '',
      additionalFollowUpTarget: '',
      additionalFollowUpEffort: '',
      additionalFollowUpPeriod: '',
      additionalSupportNeed: '',
    },
  })

  const handleClickTargetButton = () => {
    if (disableTracker) return

    trackEvent({
      activity_type: 'pelaksanaan_click_form_c_tujuan_formulir',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: CURRENT_SCREEN_NAME,
    })
  }

  const handleClickTargetUnderstandButton = () => {
    if (disableTracker) return

    trackEvent({
      activity_type: 'pelaksanaan_click_form_c_saya_mengerti_tujuan_formulir',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: CURRENT_SCREEN_NAME,
    })
  }

  const handleClickFillingGuide = () => {
    if (disableTracker) return

    trackEvent({
      activity_type: 'pelaksanaan_click_form_c_panduan_pengisian',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: CURRENT_SCREEN_NAME,
    })
  }

  const handleClickObservationButton = () => {
    if (disableTracker) return

    trackEvent({
      activity_type: 'pelaksanaan_click_form_c_cek_penilaian_observasi',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: CURRENT_SCREEN_NAME,
    })
  }

  const handleClickObservationBackButton = () => {
    if (disableTracker) return

    trackEvent({
      activity_type: 'pelaksanaan_click_form_c_cek_penilaian_observasi_back',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: CURRENT_SCREEN_NAME,
    })
  }

  const handleClickSubmitButton = () => {
    if (!disableTracker) {
      trackEvent({
        activity_type: 'pelaksanaan_click_form_c_kumpulkan',
        screen_name: CURRENT_SCREEN_NAME,
        event_trigger: CURRENT_SCREEN_NAME,
      })
    }

    setSubmitDialogOpen(true)
  }

  const handleClickCancelSubmitDialog = () => {
    if (!disableTracker) {
      trackEvent({
        activity_type: 'pelaksanaan_click_form_c_cek_ulang_popup',
        screen_name: CURRENT_SCREEN_NAME,
        event_trigger: CURRENT_SCREEN_NAME,
      })
    }

    setSubmitDialogOpen(false)
  }

  const { tipe } = useKinerjaTipe()
  const { data: dataPelaksanaanProgress } = useAPIPelaksanaanProgress({
    ptkId,
    tipe,
    periodParams,
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const isEvaluated =
    dataPelaksanaanProgress?.data?.evaluationDocumentState === 'READY'

  const { isLoading: isLoadingFollowUpDocument, data: followUpDocumentData } =
    useAPIGetFollowUpDocument({
      params: {
        ptkId,
        tipe,
      },
      periodParams,
      options: {
        useErrorBoundary: false,
        retry: false,
        onSuccess: (res) => {
          const { followUpTargets, ...followUpDocument } = res?.data?.detail

          const formData: IFollowUpDocumentForm = {
            ...followUpDocument,
            followUpTargets: followUpTargets.map(({ id, learningModule }) => ({
              id,
              learningModuleId: learningModule?.id,
            })),
          }

          // Set data from BE as initial form data
          methods.reset(formData)
        },
      },
    })

  const upsertMutation = useAPIUpsertFollowUpDocument({
    useErrorBoundary: false,
  })

  const [isSubmitDialogOpen, setSubmitDialogOpen] = useState(false)
  const [isSaveToDraftDialogOpen, setSaveToDraftDialogOpen] = useState(false)

  const isSubmitted = followUpDocumentData?.data?.state === 'SUBMITTED'

  const isSaveDraftDisabled =
    !methods.formState.isDirty ||
    upsertMutation.isLoading ||
    isLoadingFollowUpDocument ||
    isSubmitted ||
    isEvaluated ||
    isViewOnly
  const isSubmitDisabled =
    !methods.formState.isValid ||
    upsertMutation.isLoading ||
    isLoadingFollowUpDocument ||
    isSubmitted ||
    isEvaluated ||
    isViewOnly

  const handleSaveToDraft = () => {
    if (isSaveDraftDisabled) return

    if (!disableTracker) {
      trackEvent({
        activity_type: 'pelaksanaan_click_form_c_simpan_draf',
        screen_name: CURRENT_SCREEN_NAME,
        event_trigger: CURRENT_SCREEN_NAME,
      })
    }

    upsertMutation.mutate(
      { state: 'DRAFT', detail: methods.getValues(), tipe },
      {
        onSuccess: () => {
          setSaveToDraftDialogOpen(false)
          router.replace(`${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/beranda`)

          toast({ message: 'Berhasil simpan draf!', type: 'success' })
        },
        onError: (err: CustomError) => {
          setSaveToDraftDialogOpen(false)

          const message = 'Gagal simpan draf. Coba lagi'
          const errorCode = getErrorCode(err)

          toast({
            message: generateErrorMessage(message, errorCode),
          })
        },
      }
    )
  }

  const handleSubmit = methods.handleSubmit((detail: IFollowUpDocumentForm) => {
    if (isSubmitDisabled) return

    if (!disableTracker) {
      trackEvent({
        activity_type: 'pelaksanaan_click_form_c_jawab_pertanyaan_1',
        screen_name: CURRENT_SCREEN_NAME,
        event_trigger: CURRENT_SCREEN_NAME,
      })
      trackEvent({
        activity_type: 'pelaksanaan_click_form_c_jawab_pertanyaan_2',
        screen_name: CURRENT_SCREEN_NAME,
        event_trigger: CURRENT_SCREEN_NAME,
      })
      trackEvent({
        activity_type: 'pelaksanaan_click_form_c_jawab_pertanyaan_3',
        screen_name: CURRENT_SCREEN_NAME,
        event_trigger: CURRENT_SCREEN_NAME,
      })
    }

    upsertMutation.mutate(
      { state: 'SUBMITTED', detail, tipe },
      {
        onSuccess: () => {
          setSubmitDialogOpen(false)
          queryClient.invalidateQueries(API_GET_FOLLOW_UP_DOCUMENT_QUERY_KEY)

          router.replace(`${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/beranda`)

          toast({
            message: 'Berhasil kirim Rencana Tindak Lanjut!',
            type: 'success',
          })
        },
        onError: (err: CustomError) => {
          setSubmitDialogOpen(false)

          const message = 'Gagal kirim Rencana Tindak Lanjut. Coba lagi'
          const errorCode = getErrorCode(err)

          toast({
            message: generateErrorMessage(message, errorCode),
          })
        },
      }
    )
  })

  const handleClickConfirmSubmitDialog = () => {
    if (!disableTracker) {
      trackEvent({
        activity_type: 'pelaksanaan_click_form_c_kumpulkan_popup',
        screen_name: CURRENT_SCREEN_NAME,
        event_trigger: CURRENT_SCREEN_NAME,
      })
    }
    handleSubmit()
  }

  const handleBackSave = useCallback(() => {
    if (isSaveDraftDisabled) return true
    upsertMutation.mutate({ state: 'DRAFT', detail: methods.getValues(), tipe })
    return true
  }, [isSaveDraftDisabled, methods, upsertMutation, tipe])

  useBeforePopState(router, handleBackSave)

  return (
    <div className="px-8 pb-8 pt-14 bg-gray-10 min-h-full">
      <div className="max-w-6xl mx-auto">
        <Header
          title={
            PENGELOLAAN_KINERJA_PELAKSANAAN_STEPS['DOCUMENT_FOLLOW_UP'].title
          }
          className="mb-9"
          onBack={() => {
            if (isSaveDraftDisabled) {
              router.back()
            } else {
              setSaveToDraftDialogOpen(true)
            }
          }}
        />
      </div>

      {isLoadingFollowUpDocument ? (
        <div className="w-full flex justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="max-w-5xl mx-auto">
          <section className="p-6 space-y-3 bg-white rounded-lg mb-9">
            <Text className="!text-black" variant="heading-sm" as="h2">
              Langkah-langkah:
            </Text>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Text className="!text-blue-60" variant="action">
                  Lakukan Refleksi
                </Text>
                <Text>Berdasarkan Hasil Observasi.</Text>
              </div>

              <div className="flex space-x-3">
                <Icon color="informational">arrow_circle_right</Icon>
                <div>
                  <Text className="!text-blue-60" variant="action">
                    Diskusi dengan Kepala Sekolah
                  </Text>
                  <Text>Agar lebih mudah isi Rencana Tindak Lanjut.</Text>
                </div>
              </div>

              <div className="flex space-x-3">
                <Icon color="informational">arrow_circle_right</Icon>
                <div>
                  <Text className="!text-blue-60" variant="action">
                    Lakukan Tindak Lanjut
                  </Text>
                  <Text>Untuk membantu Anda meningkatkan kinerja.</Text>
                </div>
              </div>
            </div>
          </section>

          <FormProvider {...methods}>
            <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
              <FormTindakLanjutObservasiKelas
                isDisabled={isSubmitted || isEvaluated}
                isReadOnly={isViewOnly}
                periodParams={periodParams}
                onClickTargetButton={handleClickTargetButton}
                onClickUnderstandTargetButton={
                  handleClickTargetUnderstandButton
                }
                onClickFillingGuide={handleClickFillingGuide}
                onClickObservationButton={handleClickObservationButton}
                onClickObservationBackButton={handleClickObservationBackButton}
              />
              <FormPilihanBelajarLainnya
                isDisabled={isSubmitted || isEvaluated}
                isReadOnly={isViewOnly}
              />
              <FormPilihanBelajarRuangGTK
                isDisabled={isSubmitted || isEvaluated}
                isReadOnly={isViewOnly}
                ptkId={ptkId}
                hideIfOptional={isSubmitted}
                followUpTargets={
                  followUpDocumentData?.data?.detail?.followUpTargets ?? []
                }
                periodParams={periodParams}
              />

              {(!isSubmitted || !isEvaluated) && !isViewOnly && (
                <Card>
                  <CardBody className="flex space-x-3 p-6 justify-end">
                    <Button
                      size="lg"
                      color="white"
                      disabled={isSaveDraftDisabled}
                      isLoading={upsertMutation.isLoading}
                      aria-haspopup={true}
                      aria-expanded={isSaveToDraftDialogOpen}
                      onClick={() => setSaveToDraftDialogOpen(true)}
                    >
                      Simpan draf
                    </Button>
                    <Button
                      size="lg"
                      color="blue"
                      disabled={isSubmitDisabled}
                      isLoading={upsertMutation.isLoading}
                      aria-haspopup={true}
                      aria-expanded={isSubmitDialogOpen}
                      onClick={handleClickSubmitButton}
                    >
                      Kirim
                    </Button>
                  </CardBody>
                </Card>
              )}
            </form>
          </FormProvider>

          <SubmitConfirmationDialog
            isOpen={isSubmitDialogOpen}
            onCancel={handleClickCancelSubmitDialog}
            isLoading={upsertMutation.isLoading}
            onConfirm={handleClickConfirmSubmitDialog}
          />

          <SaveToDraftDialog
            isOpen={isSaveToDraftDialogOpen}
            isLoading={upsertMutation.isLoading}
            onCancel={() => setSaveToDraftDialogOpen(false)}
            onConfirm={handleSaveToDraft}
          />
        </div>
      )}
    </div>
  )
}
